import { produce } from 'immer';
import { Emoji, generateUuid } from '@chat/utils';
import { ImState } from '@/typings/reducer';
import { IMSTATUS, MESSAGE_TYPE, MSGTYPE } from '@/config/enum';
import {
    IM_CTX,
    IM_CUSTOM_MESSAGE,
    IM_LOCAL_VIDEO_FILE,
    IM_MESSAGE,
    IM_MESSAGE_VIDEO
} from '@/typings/global';
import { PAYLOAD_LOCK, PAYLOAD_STATUS } from '@/typings/payload';
import { FAQ_TYPE } from '@/redux/faq/actionType';
import { decodeCustomMessageEmoji } from '@/utils/customMessageEmoji';
import { IMModifyAction, IM_TYPE } from './actionType';

const emoji = new Emoji();
const normalizeMessageEmoji = (payload: IM_MESSAGE): IM_MESSAGE['message'] => {
    if (payload.msg_type === 'ssbq_custom_card_msg') {
        console.log('receive custom message', payload);
    }
    if (payload.msg_type === 'ssbq_custom_card_msg' && _.isObject(payload.message)) {
        return payload.message as IM_CUSTOM_MESSAGE;
    }
    return _.isObject(payload.message) ? payload.message : emoji.entitiestoUtf16(payload.message);
};
// 通知类
const _notice = (state: ImState, payload: IM_MESSAGE) => {
    const { message_list } = state;
    const { message, __t, __id } = payload;
    const MSG = state.MSGTPL[__t];
    if (_.isObject(message)) {
        return;
    }
    if (!MSG) {
        return;
    }
    if (__t === MSGTYPE.NOTICE) {
        MSG[1] = message;
    }
    const _type = MSG[0];
    const _msg = MSG[1];
    const _replace = _msg.replace('%s', message);
    // 消息唯一id
    const uniq = `MSG-${__t}-${__id || '-'}`;
    // 是否存在相同消息
    const _same = message_list.filter((item) => item.__id === uniq);
    // 排队人数初始化
    if (__t === MSGTYPE.WAITING_QUEUE && _same.length === 0) {
        state.queue = message;
    }
    // 存在相同通知情况下，选择客服以及接入作息，即使为相同会话内，也会渲染多次
    if (
        _same.length !== 0 &&
        // 选择客服
        __t !== MSGTYPE.NOTICE &&
        // 作息
        __t !== MSGTYPE.ACCEPT
    ) {
        // 转接提示文案只渲染第一次转接的
        if (__t === MSGTYPE.TRANSFERED) {
            return;
        }
        // 插队控制
        if (uniq === `MSG-${MSGTYPE.WAITING_QUEUE}-${__id}`) {
            const _cur = (_same[0]?.message as string).match(/>([\d\\.]+)</)[1];
            if (parseInt(_cur, 10) <= parseInt(message, 10)) {
                return;
            }
            state.queue = message;
        }
        const uniq_msg = message_list.filter((item) => item.__id === uniq)[0];
        if (uniq_msg) {
            uniq_msg.type = _type;
            uniq_msg.message = _replace;
        }
        return;
    }
    message_list.push({
        ...payload,
        type: _type,
        message: _replace,
        id: 0,
        __id: uniq
    });
};
// 消息撤回
const _revoke = (state, payload) => {
    const { message_list } = state;
    const revoke = message_list.filter((item) => item.id === parseInt(payload.message, 10));
    if (revoke.length !== 0) {
        const revoke_msg = revoke[0];
        revoke_msg.time = dayjs(payload.time * 1000).format('YYYY-MM-DD HH:mm:ss');
        revoke_msg.type = MESSAGE_TYPE.revoke;
    }
};

// 消息审核
const _audit = (state, payload) => {
    const { message_list } = state;
    const audit = message_list.filter((item) => item.id === parseInt(payload.message, 10));
    if (audit.length !== 0) {
        const audit_msg = audit[0];
        // audit_msg.type = 'out';
        audit_msg.msg_type = 'audit';
    }
};

// 真实视频消息回填本地占位视频消息id和头像
const _syncVideoMessage = (state: ImState, payload: IM_MESSAGE) => {
    const { message_list } = state;
    if (!_.isObject(payload.message)) {
        return;
    }
    const videoMessage = payload.message as IM_MESSAGE_VIDEO;
    const tempVideoIndex = _.findIndex(
        message_list,
        (item) =>
            item.msg_type === '_video' &&
            item.message instanceof File &&
            (item.message as IM_LOCAL_VIDEO_FILE).uuid === videoMessage.uuid
    );
    if (tempVideoIndex === -1) {
        return;
    }
    message_list[tempVideoIndex] = {
        ...message_list[tempVideoIndex],
        id: payload.id,
        avatar: payload.avatar
    };
};

// 设置聊天阶段各个状态
const _setChatStatus = (state: ImState, payload: PAYLOAD_STATUS) =>
    produce(state, (draft) => {
        const { STATUS } = draft;
        STATUS[payload.name] = payload.value;
    });
// 重新发送消息并删除发送失败的消息
const _resendMessage = (state: ImState, payload: string) => {
    const { message_list } = state;
    const new_list = message_list.filter((item) => {
        if (item.type !== 'offline') {
            return item;
        }
        return (item.ctx as IM_CTX).key !== payload;
    });
    return {
        ...state,
        message_list: new_list
    };
};
// 设置按钮锁定状态
const _setLockStatus = (state: ImState, payload: PAYLOAD_LOCK) =>
    produce(state, (draft) => {
        const { lock } = draft;
        lock[payload.name] = payload.value;
    });
const INITIAL_STATE: ImState = {
    contact: 0,
    guest: true,
    user_head_img: '',
    user_nick: '',
    queue: '0',
    stage: 0,
    nav_show: 0,
    start: {
        auto_start: 0
    },
    actived: 0,
    started: 0,
    count: 0,
    platform_id: 0,
    STATUS: {
        status: IMSTATUS.STARTED, // 当前状态
        EVALUATED: 0, // 评论状态
        EVAL_INVITED: 0, // 是否被邀请
        CLOSED: 0, // 是否关闭
        EVALUATED_BY_CLOSE: 0, // 退出之后是否弹出评价
        COMMENTED: 0 // 留言
    },
    lock: {
        comment: 0
    },
    MSGTPL: {},
    sess_id: 0,
    prefix: '',
    last_time: 0,
    last_id: 0,
    message_list: [],
    user_contact: [],
    video_lock: false,
    video_temp_list: [],
    video_param: null
};
const actionHandler: IMModifyAction = {
    [IM_TYPE.SET_MESSAGE_LIST]: (state, payload) =>
        produce(state, (draft) => {
            const { message_list } = draft;
            const { type = '' } = payload;
            // msg中混有无type的消息，这里做过滤操作
            if (!type) {
                return;
            }
            // 消息撤回
            if (type === MESSAGE_TYPE.revoke) {
                _revoke(draft, payload);
                return;
            }
            // 消息审核
            if (type === MESSAGE_TYPE.audit) {
                _audit(draft, payload);
                return;
            }
            if (!_.has(MESSAGE_TYPE, type)) {
                _notice(draft, payload);
                return;
            }
            // 视频消息
            if (payload.msg_type === 'video') {
                _syncVideoMessage(draft, payload);
            }
            message_list.push({
                ...payload,
                // emoji及表情转换
                message: normalizeMessageEmoji(payload),
                __t: payload.__t || payload.type,
                avatar: payload.avatar,
                nick: payload.nick
            });
        }),
    [FAQ_TYPE.SET_FAQ_MESSAGE]: (state, payload) =>
        produce(state, (draft) => {
            const { message_list } = draft;
            const idx_loading = _.findIndex(message_list, (msg) => msg.type === 'faq-loading');
            const item = {
                id: generateUuid(8, 16),
                ...payload,
                message: normalizeMessageEmoji(payload),
                __t: payload.type,
                avatar: 'faq',
                nick: 'faq'
            };

            // 如果新消息是loading，替换最新loading消息
            if (payload.type === 'faq-loading') {
                if (idx_loading !== -1) {
                    message_list[idx_loading] = item;
                    return;
                }
            }

            // 按id将loading替换为答案，使用答案自身的时间
            if (payload.id) {
                const idx = _.findIndex(message_list, (msg) => msg.id === payload.id);
                if (idx !== -1) {
                    message_list[idx] = item;
                    return;
                }
            }
            // 如果消息列表存在 loading 消息, 插入到loading消息之前
            if (idx_loading !== -1) {
                message_list.splice(idx_loading, 0, item);
                return;
            }

            message_list.push(item);
        }),
    [FAQ_TYPE.UPDATE_FAQ_SERVER_CONTENT]: (state, payload) =>
        produce(state, (draft) => {
            const target = _.find(draft.message_list, (item) => item.id === payload.id);
            if (target) {
                target.server_content = payload.content;
            }
        }),
    [FAQ_TYPE.REMOVE_FAQ_LOADING]: (state) =>
        produce(state, (draft) => {
            draft.message_list = _.filter(draft.message_list, (msg) => msg.type !== 'faq-loading');
        }),
    [IM_TYPE.SET_NOTICE_LIST]: (state, payload) => ({
        ...state,
        message_list: [...state.message_list, payload as IM_MESSAGE]
    }),
    [IM_TYPE.SET_LAST]: (state, payload) => ({
        ...state,
        last_id: payload.last_id || state.last_id,
        last_time: payload.last_time || state.last_time
    }),
    [IM_TYPE.SET_INDEX_CONFIG]: (state, payload) => ({
        ...state,
        ...payload
    }),
    [IM_TYPE.SET_SESSION]: (state, payload) => ({
        ...state,
        ...payload,
        MSGTPL: { ...state.MSGTPL, ...payload.MSGTPL }
    }),
    [IM_TYPE.SET_START_SESSION]: (state, payload) => ({
        ...state,
        ...payload
    }),

    [IM_TYPE.SET_WAKEUP_CHAT]: (state, payload) => ({
        ...state,
        actived: payload.actived,
        started: payload.started
    }),
    [IM_TYPE.SET_CHAT_STATUS]: (state, payload) => _setChatStatus(state, payload),
    [IM_TYPE.RESEND_MESSAGE]: (state, payload) => _resendMessage(state, payload),
    [IM_TYPE.SET_MESSAGE_COUNT]: (state) => ({
        ...state,
        count: state.count + 1
    }),
    [IM_TYPE.SET_STAGE_STATUS]: (state, payload) => ({
        ...state,
        stage: payload
    }),
    [IM_TYPE.RELOAD_IM]: (state) =>
        produce(state, (draft) => {
            const { STATUS } = draft;
            STATUS.status = IMSTATUS.STARTED;
            STATUS.CLOSED = 0;
            STATUS.EVALUATED = 0;
            STATUS.EVALUATED_BY_CLOSE = 0;
            STATUS.EVAL_INVITED = 0;
            STATUS.COMMENTED = 0;
            draft.last_time = 0;
            draft.last_id = 0;
            draft.stage = 0;
            draft.count = 0;
        }),
    [IM_TYPE.SET_NAV_VISIBLE]: (state, payload) => ({
        ...state,
        nav_show: payload
    }),
    [IM_TYPE.SET_LOCK_STATUS]: (state, payload) => _setLockStatus(state, payload),

    [IM_TYPE.SET_VIDEO_LOCK]: (state, payload) => ({
        ...state,
        video_lock: payload
    }),
    [IM_TYPE.SET_VIDEO_PARAM]: (state, payload) => ({
        ...state,
        video_param: payload
    }),
    [IM_TYPE.SET_VIDEO_TEMP_LIST]: (state, payload) => ({
        ...state,
        video_temp_list: [...state.video_temp_list, payload]
    }),
    [IM_TYPE.SET_VIDEO_MESSAGE_UUID]: (state, payload) =>
        produce(state, (draft) => {
            const { file, uuid } = payload;
            const index = _.findIndex(
                draft.message_list,
                (item) =>
                    item.msg_type === '_video' &&
                    item.message instanceof File &&
                    item.message === file
            );
            if (index === -1) {
                return;
            }
            const currentFile = draft.message_list[index].message as IM_LOCAL_VIDEO_FILE;
            if (currentFile.uuid) {
                return;
            }
            const nextFile = Object.assign(currentFile, { uuid });
            draft.message_list[index] = {
                ...draft.message_list[index],
                message: nextFile
            };
        }),
    [IM_TYPE.SET_USER_CONTACT]: (state, payload) => {
        const user_contact = [...state.user_contact];
        const index = _.findIndex(user_contact, { key: payload.key });
        if (index > -1) {
            user_contact[index] = payload;
        } else {
            user_contact.push(payload);
        }
        return {
            ...state,
            user_contact
        };
    },
    [IM_TYPE.SET_DEFAULT_STATE]: () => INITIAL_STATE
};

export default function im(state = INITIAL_STATE, action: { type: string; payload: any }): ImState {
    return actionHandler[action.type] ? actionHandler[action.type](state, action.payload) : state;
}
